/**
 * PainelFunnelCard — KPI card fiel ao prototipo M3 Expressive.
 * Escopo: exclusivo da tela Painel (DashboardFourPsPage).
 * Prefixo Painel obrigatorio (isolamento de telas em paralelo).
 *
 * Renderiza:
 *   - kpi-label (uppercase, variant)
 *   - kpi-num + delta pill
 *   - sparkline OU barra horizontal (bar > 0)
 *   - descricao amigavel
 *   - source chip + link "ver origem"
 *   - estados: valor ausente exibe "—" + flag honesta
 *
 * A11y: button quando clicavel (role implicito), aria-label descritivo.
 * Zero string hardcoded — todas as strings vem de props (chamador usa t()).
 */

import { useEffect, useRef } from 'react';
import { Sparkline } from './Sparkline';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type PainelSourceKey = 'ig' | 'ads' | 'drive' | 'other';

export interface PainelFunnelCardProps {
  /** Identificador unico para key de lista. */
  readonly id: string;
  /** Rotulo do KPI — uppercase pequeno. */
  readonly label: string;
  /** Valor formatado ("1.274.054", "R$ 0,12", "—"). Null = sem dado. */
  readonly value: string | null;
  /** Delta percentual: positivo, negativo ou null = sem historico. */
  readonly delta?: number | null;
  /** Sparkline — array de pontos normalizados. Mutual exclusive com barPct. */
  readonly series?: readonly number[] | undefined;
  /** Barra horizontal de progresso (0-100). Mutual exclusive com series. */
  readonly barPct?: number | undefined;
  /** Descricao amigavel para o dono (nao-tecnico). */
  readonly description?: string | undefined;
  /** Nota de origem do dado para o modal. */
  readonly note?: string | undefined;
  /** Fonte do dado. */
  readonly source: PainelSourceKey;
  /** Label traduzido da fonte (ex: "Instagram", "Meta Ads"). */
  readonly sourceLabel: string;
  /** Label traduzido "ver origem" para o link. */
  readonly linkLabel: string;
  /** Label traduzido "sem dado" para o flag de vazio. */
  readonly noDataLabel: string;
  /** Bento span — numero de colunas (3, 4, 6, 12). Default: 4. */
  readonly span?: 3 | 4 | 6 | 12;
  /** Variante visual do card. Default: 'default'. */
  readonly variant?: 'default' | 'hero-primary' | 'warn-tertiary';
  /** Delay de animacao de entrada em ms. */
  readonly animDelay?: number;
  /** Callback ao clicar no card (abre drilldown/modal). */
  readonly onClick?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers de delta
// ---------------------------------------------------------------------------

function deltaPillClass(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return 'painel-delta-pill painel-dp-none';
  if (delta > 0) return 'painel-delta-pill painel-dp-up';
  if (delta < 0) return 'painel-delta-pill painel-dp-down';
  return 'painel-delta-pill painel-dp-flat';
}

function deltaPillLabel(delta: number | null | undefined, noDataLabel: string): string {
  if (delta === null || delta === undefined) return noDataLabel;
  if (delta === 0) return 'estavel';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta}%`;
}

function deltaAriaLabel(delta: number | null | undefined, noDataLabel: string): string {
  if (delta === null || delta === undefined) return noDataLabel;
  const sign = delta > 0 ? 'subiu' : 'caiu';
  const abs = Math.abs(delta);
  if (delta === 0) return 'estavel';
  return `${sign} ${abs}%`;
}

// Delta icon via Material Symbols
function DeltaIcon({ delta }: { readonly delta: number | null | undefined }): JSX.Element | null {
  if (delta === null || delta === undefined || delta === 0) return null;
  const icon = delta > 0 ? 'trending_up' : 'trending_down';
  return (
    <span
      className="material-symbols-rounded"
      style={{ fontSize: 11, verticalAlign: -1 }}
      aria-hidden="true"
    >
      {icon}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Barra horizontal inline (sem canvas/SVG complexo)
// ---------------------------------------------------------------------------

function PainelInlineBar({
  pct,
  animDelay = 0,
}: {
  readonly pct: number;
  readonly animDelay?: number;
}): JSX.Element {
  const fillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = fillRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      el.classList.add('painel-bar-animate');
    }, animDelay + 120);
    return () => clearTimeout(t);
  }, [animDelay]);

  return (
    <div className="painel-bar-wrap" aria-hidden="true">
      <div className="painel-bar-track">
        <div
          ref={fillRef}
          className="painel-bar-fill"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function PainelFunnelCard({
  id: _id,
  label,
  value,
  delta,
  series,
  barPct,
  description,
  source,
  sourceLabel,
  linkLabel,
  noDataLabel,
  span = 4,
  variant = 'default',
  animDelay = 0,
  onClick,
}: PainelFunnelCardProps): JSX.Element {
  const displayValue = value === null || value === undefined || value === 'N/A' ? null : value;
  const hasBar = barPct !== undefined && barPct > 0;
  const hasSpark = !hasBar && series !== undefined && series.length >= 2;

  const sourceClass =
    source === 'ig'
      ? 'painel-dot-ig'
      : source === 'ads'
        ? 'painel-dot-ads'
        : source === 'drive'
          ? 'painel-dot-drive'
          : 'painel-dot-drive';

  const cardClass = ['painel-kpi-card', `span-${span}`, variant !== 'default' ? variant : '']
    .filter(Boolean)
    .join(' ');

  const ariaLabel = `${label}: ${displayValue ?? noDataLabel}. ${deltaAriaLabel(delta, noDataLabel)}. Fonte: ${sourceLabel}.`;

  const inner = (
    <>
      <div className="painel-kpi-label">{label}</div>

      <div className="painel-kpi-num-row">
        <span className="painel-kpi-num">{displayValue !== null ? displayValue : '—'}</span>
        <span className={deltaPillClass(delta)} aria-label={deltaAriaLabel(delta, noDataLabel)}>
          <DeltaIcon delta={delta} />
          {deltaPillLabel(delta, noDataLabel)}
        </span>
      </div>

      {displayValue === null ? (
        <span className="painel-flag-no-data" aria-live="polite">
          {noDataLabel}
        </span>
      ) : null}

      {hasBar ? (
        <PainelInlineBar pct={barPct as number} animDelay={animDelay} />
      ) : hasSpark ? (
        <div className="painel-spark-area">
          <Sparkline
            points={series as readonly number[]}
            width={120}
            height={30}
            ariaLabel={label}
          />
        </div>
      ) : null}

      {description !== undefined && description.length > 0 ? (
        <p className="painel-kpi-desc">{description}</p>
      ) : null}

      <div className="painel-kpi-footer">
        <span className="painel-source-chip">
          <span className={`painel-src-dot ${sourceClass}`} aria-hidden="true" />
          {sourceLabel}
        </span>
        <button
          type="button"
          className="painel-kpi-link"
          aria-label={`${linkLabel} — ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
        >
          {linkLabel}
        </button>
      </div>
    </>
  );

  if (onClick !== undefined) {
    return (
      <button
        type="button"
        className={cardClass}
        style={{ animationDelay: `${animDelay}ms` }}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {inner}
      </button>
    );
  }

  return (
    <article className={cardClass} style={{ animationDelay: `${animDelay}ms` }}>
      {inner}
    </article>
  );
}
